import React, { useEffect, useState, useRef } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X, ChevronDown, CalendarDays } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import PillButton from "./PillButton";

const EXPERTISES = [
    { label: "Création de site web", to: "/creation-site-web", desc: "Sites vitrines qui convertissent" },
    { label: "Référencement local (SEO)", to: "/referencement-local-seo", desc: "Apparaître quand on cherche votre métier" },
    { label: "Identité visuelle", to: "/identite-visuelle", desc: "Logo, charte, photos pro" },
];
const METIERS = [
    { label: "Site web pour plombiers", to: "/site-web-plombier" },
    { label: "Site web pour couvreurs RGE", to: "/site-web-couvreur-rge" },
    { label: "Site web pour électriciens", to: "/site-web-electricien" },
];
const TOPNAV = [
    { label: "Méthode", to: "/methode" },
    { label: "Tarifs", to: "/tarifs" },
    { label: "Agence", to: "/agence" },
    { label: "Blog", to: "/blog" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const closeTimer = useRef(null);
    const { scrollY } = useScroll();
    const navigate = useNavigate();
    const location = useLocation();

    useMotionValueEvent(scrollY, "change", (latest) => {
        setScrolled(latest > 40);
    });

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);

    useEffect(() => {
        function onClick(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
        }
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const openDropdown = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setDropdownOpen(true);
    };
    const scheduleClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setDropdownOpen(false), 180);
    };

    const isActive = (to) => location.pathname === to;

    return (
        <>
            <motion.header
                initial={{ y: -24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="fixed top-4 left-0 right-0 z-50 px-4"
                data-testid="site-header"
            >
                <div
                    className={`mx-auto flex items-center justify-between transition-all duration-500 rounded-full ${
                        scrolled
                            ? "max-w-5xl glass-pill py-2 pl-5 pr-2"
                            : "max-w-6xl bg-[#F7F7F5]/85 backdrop-blur-md border border-[#1A2F6E]/8 py-2.5 pl-5 pr-2.5"
                    }`}
                >
                    <Link
                        to="/"
                        className="font-display text-xl md:text-2xl font-bold lowercase text-[#1A2F6E] tracking-tight"
                        data-testid="logo-link"
                    >
                        alva digital
                    </Link>

                    <nav className="hidden lg:flex items-center gap-0.5" aria-label="Navigation principale">
                        <div
                            ref={dropdownRef}
                            className="relative"
                            onMouseEnter={openDropdown}
                            onMouseLeave={scheduleClose}
                        >
                            <button
                                type="button"
                                onClick={() => setDropdownOpen((v) => !v)}
                                className="flex items-center gap-1 px-4 py-2 text-sm text-[#1A2F6E]/80 hover:text-[#2B7BE4] transition-colors rounded-full hover:bg-[#1A2F6E]/5"
                                aria-haspopup="true"
                                aria-expanded={dropdownOpen}
                                data-testid="nav-expertises"
                            >
                                Expertises
                                <ChevronDown size={14} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                            </button>

                            {dropdownOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.18 }}
                                    className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[480px] rounded-2xl bg-white border border-[#1A2F6E]/10 p-4 shadow-[0_24px_60px_-12px_rgba(26,47,110,0.18)]"
                                    role="menu"
                                    data-testid="expertises-dropdown"
                                >
                                    <div className="grid gap-1">
                                        {EXPERTISES.map((it) => (
                                            <Link
                                                key={it.to}
                                                to={it.to}
                                                onClick={() => setDropdownOpen(false)}
                                                className="flex flex-col rounded-xl px-4 py-3 hover:bg-[#EEF2FB] transition-colors group"
                                                role="menuitem"
                                                data-testid={`dropdown-${it.to.replace("/", "")}`}
                                            >
                                                <span className="text-sm font-semibold text-[#1A2F6E] group-hover:text-[#2B7BE4]">{it.label}</span>
                                                <span className="text-xs text-[#1A1A2E]/55 mt-0.5">{it.desc}</span>
                                            </Link>
                                        ))}
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-[#1A2F6E]/8">
                                        <p className="px-4 text-[10px] font-bold uppercase tracking-[0.2em] text-[#2B7BE4] mb-2">
                                            Sites par métier
                                        </p>
                                        <div className="grid gap-1">
                                            {METIERS.map((it) => (
                                                <Link
                                                    key={it.to}
                                                    to={it.to}
                                                    onClick={() => setDropdownOpen(false)}
                                                    className="flex items-center justify-between rounded-xl px-4 py-2 text-sm text-[#1A2F6E]/80 hover:text-[#2B7BE4] hover:bg-[#EEF2FB] transition-colors"
                                                    role="menuitem"
                                                    data-testid={`dropdown-${it.to.replace("/", "")}`}
                                                >
                                                    {it.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        {TOPNAV.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`px-4 py-2 text-sm transition-colors rounded-full hover:bg-[#1A2F6E]/5 ${
                                    isActive(item.to) ? "text-[#2B7BE4] font-semibold" : "text-[#1A2F6E]/80 hover:text-[#2B7BE4]"
                                }`}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    <div className="flex items-center gap-1.5 md:gap-2">
                        <Link
                            to="/audit-gratuit"
                            className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-[#1A2F6E] hover:text-[#2B7BE4] transition-colors"
                            data-testid="header-rdv"
                        >
                            <CalendarDays size={16} />
                            Prendre RDV
                        </Link>
                        <PillButton
                            as={Link}
                            to="/audit-gratuit"
                            variant="primary"
                            size="sm"
                            className="hidden sm:inline-flex"
                            data-testid="header-cta"
                        >
                            Nous contacter
                        </PillButton>
                        <button
                            className="lg:hidden inline-flex items-center justify-center min-w-[44px] min-h-[44px] p-2 rounded-full bg-[#1A2F6E]/8 text-[#1A2F6E]"
                            onClick={() => setMenuOpen(true)}
                            aria-label="Ouvrir le menu"
                            data-testid="mobile-menu-open"
                        >
                            <Menu size={20} />
                        </button>
                    </div>
                </div>
            </motion.header>

            {menuOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[60] bg-[#F7F7F5]/98 backdrop-blur-xl overflow-y-auto lg:hidden"
                    data-testid="mobile-menu"
                >
                    <div className="flex items-center justify-between p-4 sticky top-0 bg-[#F7F7F5]/95 backdrop-blur">
                        <Link to="/" onClick={() => setMenuOpen(false)} className="font-display text-xl font-bold lowercase text-[#1A2F6E]">
                            alva digital
                        </Link>
                        <button
                            className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] p-2 rounded-full bg-[#1A2F6E]/8 text-[#1A2F6E]"
                            onClick={() => setMenuOpen(false)}
                            aria-label="Fermer le menu"
                            data-testid="mobile-menu-close"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <nav className="flex flex-col gap-1 p-6 pb-20" aria-label="Navigation mobile">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2B7BE4] mt-2">Expertises</p>
                        {EXPERTISES.map((it) => (
                            <Link key={it.to} to={it.to} onClick={() => setMenuOpen(false)} className="text-xl font-display font-bold text-[#1A2F6E] py-3 border-b border-[#1A2F6E]/10" data-testid={`mobile-nav-${it.to.replace("/","")}`}>
                                {it.label}
                            </Link>
                        ))}
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2B7BE4] mt-6">Sites par métier</p>
                        {METIERS.map((it) => (
                            <Link key={it.to} to={it.to} onClick={() => setMenuOpen(false)} className="text-base font-medium text-[#1A2F6E]/85 py-2.5 border-b border-[#1A2F6E]/10">
                                {it.label}
                            </Link>
                        ))}
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2B7BE4] mt-6">Plus</p>
                        {TOPNAV.map((it) => (
                            <Link key={it.to} to={it.to} onClick={() => setMenuOpen(false)} className="text-xl font-display font-bold text-[#1A2F6E] py-3 border-b border-[#1A2F6E]/10" data-testid={`mobile-nav-${it.label.toLowerCase()}`}>
                                {it.label}
                            </Link>
                        ))}
                        <PillButton
                            as={Link}
                            to="/audit-gratuit"
                            className="mt-6 w-full"
                            size="lg"
                            variant="primary"
                            onClick={() => setMenuOpen(false)}
                            data-testid="mobile-cta"
                        >
                            Nous contacter
                        </PillButton>
                    </nav>
                </motion.div>
            )}
        </>
    );
}
